// ─────────────────────────────────────────────────────────────────────────────
// Day 6 — Supabase-backed wrapper around the PURE completion state machine
// (src/lib/completion.ts). This file does the I/O; completion.ts does the logic.
//
// All reads/writes go through the browser anon key under RLS (the same pattern
// the rest of the SPA uses). The certificate PDF + portal export are the only
// flows that use the service-role Worker path; persisting completion from the
// student's own session is fine under the existing RLS (a student owns their
// enrollment / progress / assignment-status rows).
// ─────────────────────────────────────────────────────────────────────────────

import { supabase } from "./supabase";
import { computeCompletion, DEFAULT_ALL_REQUIRED } from "./completion";
import type {
  CompletionResult,
  RequiredLessonState,
  RequiredAssignmentState,
} from "./completion";
import type {
  LessonRow,
  ModuleRow,
  LessonProgressRow,
  AssignmentSubmissionStatusRow,
  EnrollmentRow,
} from "./types";

export interface EnrollmentCompletion {
  result: CompletionResult;
  /** True when no lesson was flagged required so we defaulted all to required. */
  defaultedAllRequired: boolean;
}

/**
 * Resolve and compute completion for one enrollment.
 *
 * Gathers: the cohort's modules → lessons, which lessons are required, the
 * student's lesson_progress, and the assignment_submission_status grade_status
 * for the required assignment lessons; then runs the pure state machine.
 *
 * Gracefully tolerates missing tables/data (returns an empty, not-complete
 * result) so the dashboard can render before migrations are applied.
 */
export async function computeCompletionForEnrollment(args: {
  enrollmentId: string;
  studentId: string;
  cohortId: string;
}): Promise<EnrollmentCompletion> {
  const { enrollmentId, studentId, cohortId } = args;

  // 1. Modules for the cohort → their lessons.
  const { data: mods } = await supabase
    .from("modules")
    .select("id, cohort_id, session_no, block_no, title, order, objectives")
    .eq("cohort_id", cohortId);
  const moduleIds = ((mods as ModuleRow[] | null) ?? []).map((m) => m.id);

  let lessons: LessonRow[] = [];
  if (moduleIds.length > 0) {
    const { data: les } = await supabase
      .from("lessons")
      .select(
        "id, module_id, type, title, order, body_ref, required_for_completion",
      )
      .in("module_id", moduleIds);
    lessons = (les as LessonRow[] | null) ?? [];
  }

  // 2. Decide what's required. If nothing is flagged, default ALL to required.
  const anyFlagged = lessons.some((l) => l.required_for_completion);
  const defaultedAllRequired = !anyFlagged && DEFAULT_ALL_REQUIRED;
  const requiredLessons = lessons.filter(
    (l) => l.required_for_completion || defaultedAllRequired,
  );

  // Split required lessons into "content-style" (completion via lesson_progress)
  // and assignment-style (completion via grade_status === 'passed').
  const isAssignmentType = (t: LessonRow["type"]) =>
    t === "workbook" || t === "assignment";
  const requiredContentLessons = requiredLessons.filter(
    (l) => !isAssignmentType(l.type),
  );
  const requiredAssignmentLessons = requiredLessons.filter((l) =>
    isAssignmentType(l.type),
  );

  // 3. lesson_progress for the content lessons.
  const contentIds = requiredContentLessons.map((l) => l.id);
  const progressMap: Record<string, LessonProgressRow> = {};
  if (contentIds.length > 0) {
    const { data: prog } = await supabase
      .from("lesson_progress")
      .select("id, student_id, lesson_id, completed_at")
      .eq("student_id", studentId)
      .in("lesson_id", contentIds);
    for (const row of (prog as LessonProgressRow[] | null) ?? []) {
      progressMap[row.lesson_id] = row;
    }
  }

  // 4. assignment_submission_status for the assignment lessons.
  const assignmentIds = requiredAssignmentLessons.map((l) => l.id);
  const statusMap: Record<string, AssignmentSubmissionStatusRow> = {};
  if (assignmentIds.length > 0) {
    const { data: st } = await supabase
      .from("assignment_submission_status")
      .select(
        "id, enrollment_id, lesson_id, status, submitted_at, auto_percent, auto_passed, grade_status, manual_pass, manual_note, graded_at, graded_by",
      )
      .eq("enrollment_id", enrollmentId)
      .in("lesson_id", assignmentIds);
    for (const row of (st as AssignmentSubmissionStatusRow[] | null) ?? []) {
      statusMap[row.lesson_id] = row;
    }
  }

  // 5. Shape the pure inputs.
  const lessonStates: RequiredLessonState[] = requiredContentLessons.map(
    (l) => ({
      lessonId: l.id,
      title: l.title,
      completedAt: progressMap[l.id]?.completed_at ?? null,
    }),
  );
  const assignmentStates: RequiredAssignmentState[] =
    requiredAssignmentLessons.map((l) => {
      const s = statusMap[l.id];
      return {
        lessonId: l.id,
        title: l.title,
        gradeStatus: s?.grade_status ?? "pending",
        resolvedAt: s?.graded_at ?? s?.submitted_at ?? null,
      };
    });

  const result = computeCompletion({
    lessons: lessonStates,
    assignments: assignmentStates,
  });

  return { result, defaultedAllRequired };
}

/**
 * Persist completion when the state machine says complete:
 *   - flips enrollments.completion_status → 'complete' and stamps completed_at,
 *   - upserts a completion_records row carrying the certificate/insurer fields.
 *
 * Idempotent: re-running on an already-complete enrollment is a no-op upsert.
 * Returns the completion_records id on success (for the certificate link).
 *
 * NOTE: this runs under the student's anon session. The completion_records RLS
 * (0005) lets a student write/read their OWN record (enrollment chain) and the
 * instructor read all. The certificate Worker reads it back with service-role.
 */
export async function persistCompletion(args: {
  enrollmentId: string;
  result: CompletionResult;
  student: { full_name: string | null; cert_number: string | null; aircraft_reg: string | null };
  cohortName: string | null;
}): Promise<{ error: string | null; completionRecordId: string | null }> {
  const { enrollmentId, result, student, cohortName } = args;

  if (!result.complete) {
    return { error: "not_complete", completionRecordId: null };
  }

  const completedAt = result.completedAtCandidate ?? new Date().toISOString();

  // Flip the enrollment.
  const { error: enrErr } = await supabase
    .from("enrollments")
    .update({ completion_status: "complete", completed_at: completedAt })
    .eq("id", enrollmentId);
  if (enrErr) return { error: enrErr.message, completionRecordId: null };

  // Upsert the completion record (unique on enrollment_id, see 0001).
  const { data, error: crErr } = await supabase
    .from("completion_records")
    .upsert(
      {
        enrollment_id: enrollmentId,
        student_name: student.full_name,
        student_cert_number: student.cert_number,
        aircraft_reg: student.aircraft_reg,
        cohort_name: cohortName,
        completed_at: completedAt,
        // course_title, hours, instructor_name default in the schema (0001).
      },
      { onConflict: "enrollment_id" },
    )
    .select("id")
    .maybeSingle();
  if (crErr) return { error: crErr.message, completionRecordId: null };

  return {
    error: null,
    completionRecordId: (data as { id: string } | null)?.id ?? null,
  };
}

/** Convenience: load the enrollment row for a student in a cohort. */
export async function fetchEnrollment(
  studentId: string,
  cohortId: string,
): Promise<EnrollmentRow | null> {
  const { data } = await supabase
    .from("enrollments")
    .select("id, student_id, cohort_id, completion_status, completed_at")
    .eq("student_id", studentId)
    .eq("cohort_id", cohortId)
    .maybeSingle();
  return (data as EnrollmentRow | null) ?? null;
}
